import { WizardStep, type StepKey } from "../wizardSteps";

// wizardInvalidation — smart "unmark downstream wizard steps" rules.
//
// Why this exists: previously every adapter / channel / bank UPDATE
// blanket-unmarked every step from Banks through BACnet (and sometimes
// further), even when the change was something cosmetic or network-only
// like editing a channel's IP address. Field engineers complained that
// fixing a typo in a socket address forced them to re-walk Notifications
// and Actions before they could click "Complete Setup" on Review, even
// though those downstream steps did not depend on the changed value.
//
// This module isolates the dependency rules. Each entity type maps each
// editable field to the set of wizard steps that genuinely depend on
// that field. computeInvalidatedSteps() diffs the old and new entity
// and returns the union of dependent steps — empty when nothing
// material changed. Call sites pass the resulting array straight into
// `unmarkStepCompleted(...)`, so a no-op edit produces a no-op unmark.
//
// Deletions are NOT covered by the RULE TABLES: when an entity is
// removed, every entity that referenced it is dangling regardless of the
// deleted entity's prior field values, so there is no field diff to
// consult. Delete call sites instead use `wizardStepsFrom(from, to?)`
// below, which returns a downstream range as an explicit `[from, to]`
// pair rather than a hand-maintained list per call site — the shape that
// let StepServer's server-delete list go stale twice. Not every delete
// call site derives its range from it yet: see the function's own doc
// comment for which callers do today and which independently compute an
// equivalent range for now. computeInvalidatedSteps and the rule tables
// refine only the UPDATE path.
//
// Adding a new editable field on an entity? Add it to the rule table
// here and write a test in wizardInvalidation.test.ts that pins which
// steps it should and should not invalidate. Forgetting to add a field
// means the diff treats it as "unchanged" and never invalidates — the
// safer default than the previous "always invalidate everything" but
// still wrong for genuine structural fields. The unit-test suite is
// the lockstep audit.

/**
 * Wizard step indices.
 *
 * Re-exported, not declared: the numbers are derived from WIZARD_STEPS in
 * ../wizardSteps.ts, so this map and the list it indexes cannot drift apart.
 * It is re-exported from here because the rule tables below are written in
 * terms of it and around forty modules already import it from this path.
 *
 * NOTE: Channel Modifiers is intentionally positioned AFTER Banks because the
 * modifier UI's source/target message dropdowns are populated from the
 * adapter(s) bound to each channel — and that channel<->adapter linkage is
 * established at the Bank step. Re-routing this step earlier would force the
 * user to pick a message before the system can know which messages are legal
 * for the chosen source/target channels.
 */
export { WizardStep };
export type { StepKey };

/**
 * Every wizard step index from `from` through `to`, inclusive. `to`
 * defaults to Review, since a whole-subtree-root deletion (the entity
 * every downstream step is ultimately hung off) is the common case; pass
 * an explicit `to` for a narrower, entity-scoped delete where later steps
 * genuinely don't depend on the deleted entity. No production call site
 * passes an explicit `to` yet: the one narrower delete in production,
 * `handleDeleteBankFloor` in StepBanks.tsx, unmarks a single step
 * (`WizardStep.Lifts`, matching `BANK_FIELD_RULES.floorCount`) directly
 * rather than calling this helper — the bank survives a floor delete, so
 * there is no downstream range to state, only one step.
 *
 * Reaching Review, and reaching Users, are NOT decided per call site by
 * "does this entity's content affect that step" — they are forced by
 * SetupWizardPage's forward-jump guard (`step <= currentStep ||
 * isStepCompleted(step) || isStepCompleted(step - 1)`): once ANY step in
 * the middle of a range must be unmarked, every step after it through
 * Review must be too, or that later stale-complete step becomes a
 * one-click hop-in target skipping everything unmarked before it. This is
 * why the adapter-, channel- and bank-delete call sites all reach Users
 * and Review even though none of the three UPDATE rule tables below ever
 * route a field change to Users — the inclusion is structural, not a
 * claim that Users & Roles content is stale. (Role scope grants are
 * per-bank with empty-means-unrestricted semantics, which independently
 * argues the same way for the bank case — see redux/scope/scopeSlice.ts.)
 *
 * This is the DELETE-path counterpart to the rule tables below: when the
 * entity a step chain hangs off is destroyed rather than edited there is no
 * field diff to consult, so a range is stated directly instead of derived
 * from a field-level diff. Callers handling a "this entity is gone" event
 * spread the result straight into `unmarkStepCompleted(...)`.
 *
 * `StepServer.tsx`'s `handleDeleteServer` calls this today at its default
 * `to`. Review is deliberately included for that call: its completion flag
 * is read generically by SetupWizardPage's breadcrumb status and by its
 * forward-jump navigation guard, so leaving it set after the server is
 * deleted shows a false "finish" checkmark and lets the operator jump
 * straight to Review past the steps that were just invalidated. Review is
 * NOT in StepReview's REQUIRED_STEPS publish gate, so that inclusion is a
 * UI-honesty fix rather than a publish-safety one. `WizardContext.tsx`'s
 * `clearStaleServerErrorAndReroute` — the OTHER "server is gone" path —
 * computes an equivalent range today through its own independent filter
 * expression rather than calling this function; the two are in sync
 * because Review currently is the last step, not because they share code.
 *
 * Total: a `from` past `to` yields [], so an out-of-range caller produces
 * a no-op unmark instead of throwing.
 */
export function wizardStepsFrom(from: number, to: number = WizardStep.Review): number[] {
  const out: number[] = [];
  for (let s = from; s <= to; s += 1) out.push(s);
  return out;
}

/**
 * Field rules for the Channel entity.
 *
 * Network/transport fields (socketAddress, baudRate, etc.) intentionally
 * map to []: banks reference channels by id, not by address, so editing
 * the IP of a TCP channel cannot break any downstream wizard step.
 *
 * Structural fields (serverId, organizationId, transport type) DO break
 * downstream steps — re-parenting a channel under a different server
 * means its banks effectively belong to the wrong tree, so banks-onward
 * gets re-verified.
 */
export const CHANNEL_FIELD_RULES: Record<string, number[]> = {
  // Cosmetic / behavioral — no downstream dependency.
  name: [],
  active: [],
  // Network and transport-tuning — the things a field engineer most
  // commonly edits. None of these change the channel's identity or the
  // shape of data that flows through it, so banks/lifts/etc. stay valid.
  socketAddress: [],
  vendorSocketAddress: [],
  routableSocketAddress: [],
  serialPath: [],
  baudRate: [],
  heartbeatFrequency: [],
  heartbeatMessageId: [],
  // Per-channel mTLS client certificate config — VTS-side connection
  // hardening only. It changes how the vendor TCP connection is secured,
  // not the shape of any data banks/lifts/notifications key off of, so it
  // carries no downstream wizard dependency.
  mtlsEnabled: [],
  mtlsSecretName: [],
  // Structural re-parenting — channel now belongs to a different tree.
  serverId: [WizardStep.Banks, WizardStep.Lifts, WizardStep.Debounce, WizardStep.Bacnet],
  organizationId: [WizardStep.Banks, WizardStep.Lifts, WizardStep.Debounce, WizardStep.Bacnet],
  // Transport type change (TCP <-> UDP <-> Serial). Banks have type-
  // sensitive socket configuration tied to the transport, so flipping
  // the channel type warrants re-verifying the banks layer.
  type: [WizardStep.Banks, WizardStep.Lifts],
  connectionType: [WizardStep.Banks, WizardStep.Lifts],
};

/**
 * Field rules for the Bank entity.
 *
 * Lift-roster fields (numberOfLifts, floorCount, etc.) invalidate the
 * Lifts step because a different lift count or floor map literally
 * changes which lift rows the next step has to render and validate.
 *
 * Message-mapping fields invalidate Notifications and Actions because
 * those steps key off the bank's per-message routing — change which
 * message goes where and the existing notification/action targets may
 * be referencing keys that no longer exist.
 *
 * Re-parenting (channelId / adapterId) is the most disruptive: the
 * bank's data shape changes wholesale.
 */
export const BANK_FIELD_RULES: Record<string, number[]> = {
  name: [],
  active: [],
  // Lift roster — directly drives the Lifts step's row count.
  numberOfLifts: [WizardStep.Lifts, WizardStep.Notifications, WizardStep.Actions],
  floorCount: [WizardStep.Lifts],
  floorBelowLobby: [WizardStep.Lifts],
  startFloorNo: [WizardStep.Lifts],
  // Per-floor door capability. Which floors have front / rear doors decides
  // which Floor Openings checkboxes the Lifts & Floors step can even enable,
  // so a door edit has to send that step back for re-verification.
  //
  // Deliberately NOT Notifications / Actions: a door change alters a floor's
  // capability, never the set of floors, so every floor an action or
  // notification targets still resolves.
  //
  // The snapshot values under these keys are NOT the bank-level front_doors /
  // rear_doors booleans — those columns are write-once at create and are
  // never touched by PUT /banks/{bankID} (see utils/bankDoorSignature.ts's
  // header comment for the verified detail). They are per-floor signatures
  // built by that module (`["<floorIndex>:1", ...]`, ordered by floorIndex).
  // valuesDiffer() JSON-compares arrays structurally, so this needs no
  // change to the diff engine below.
  frontDoors: [WizardStep.Lifts],
  rearDoors: [WizardStep.Lifts],
  // Message/lift-offset routing — Notifications and Actions read these.
  // (offsetMapping also covers what a since-removed "messageMappings" key
  // was meant to represent — that key was deleted because no such field
  // exists anywhere on the Bank entity; it was unsnapshotable dead weight.)
  offsetMapping: [WizardStep.Notifications, WizardStep.Actions],
  // Real bank column, written by all three of LiftCloud's bank-update paths,
  // but the Banks step has no editor for it — it is neither read off the
  // bank nor sent in handleUpdateBank's body. The rule is pre-wired for when
  // that editor lands; there is deliberately no snapshot entry, because a
  // value the step can never change would diff to "unchanged" forever and
  // read as coverage that does not exist.
  intraContentFlags: [WizardStep.Notifications, WizardStep.Actions],
  // Re-parenting under a different channel or adapter — wholesale break.
  channelId: [WizardStep.Lifts, WizardStep.Debounce, WizardStep.Bacnet],
  adapterId: [
    WizardStep.Lifts,
    WizardStep.Debounce,
    WizardStep.Bacnet,
    WizardStep.Notifications,
    WizardStep.Actions,
  ],
};

/**
 * Field rules for the Adapter entity.
 *
 * protocolType is the most catastrophic change — the entire message
 * vocabulary swaps, every downstream reference is suspect.
 *
 * CDO binary / template / one-hot exclusion changes affect notifications
 * and actions because those reference keys that originate from the CDO.
 *
 * Cosmetic (name, active) and metadata (description) cost nothing.
 */
export const ADAPTER_FIELD_RULES: Record<string, number[]> = {
  name: [],
  active: [],
  description: [],
  // Protocol family — biggest possible blast radius.
  protocolType: [
    WizardStep.Banks,
    WizardStep.Lifts,
    WizardStep.Debounce,
    WizardStep.Bacnet,
    WizardStep.Notifications,
    WizardStep.Actions,
  ],
  // CDO & key shape — banks/notifications/actions key off these.
  cdoBinary: [WizardStep.Banks, WizardStep.Notifications, WizardStep.Actions],
  cdoTemplate: [WizardStep.Banks, WizardStep.Notifications, WizardStep.Actions],
  cdoInput: [WizardStep.Banks, WizardStep.Notifications, WizardStep.Actions],
  oneHotExclusion: [WizardStep.Notifications, WizardStep.Actions],
  oneHotExclusionConfig: [WizardStep.Notifications, WizardStep.Actions],
  // Re-parenting.
  serverId: [WizardStep.Banks, WizardStep.Lifts, WizardStep.Debounce, WizardStep.Bacnet],
  organizationId: [WizardStep.Banks, WizardStep.Lifts, WizardStep.Debounce, WizardStep.Bacnet],
};

/**
 * Compare two values for "did this field change in a way the wizard
 * cares about?" — strict identity for primitives, structural equality
 * for arrays and objects (one level deep, JSON-stringified). FlatBuffer-
 * decoded UUID fields surface as either string or { value: U128T };
 * both round-trip through JSON so equal values still compare equal.
 *
 * Both values undefined or both null returns "equal" — a field that
 * doesn't exist on either side hasn't changed.
 */
function valuesDiffer(a: unknown, b: unknown): boolean {
  if (a === b) return false;
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;

  // Primitives that aren't === are different.
  if (typeof a !== "object" || typeof b !== "object") return true;

  // Reference types — structural compare via JSON. Cheap and good enough
  // for the small objects we're diffing (typically <100 fields). If a
  // future field has cycles or non-serializable values, add a special
  // case here.
  try {
    return JSON.stringify(a) !== JSON.stringify(b);
  } catch {
    // Cycles or BigInts that JSON.stringify can't handle. Treat as
    // changed — safer than missing a real edit.
    return true;
  }
}

/**
 * Diff oldEntity and newEntity against rules and return the sorted,
 * deduped list of wizard steps that should be unmarked. Empty when
 * nothing material changed.
 *
 * A field that's listed in rules but missing from one or both entities
 * counts as "unchanged" (both undefined). A field NOT in rules is
 * silently ignored — the safer default than auto-invalidating every
 * unrecognized field.
 */
export function computeInvalidatedSteps<T extends Record<string, unknown>>(
  oldEntity: T | null | undefined,
  newEntity: T | null | undefined,
  rules: Record<string, number[]>,
): number[] {
  if (!oldEntity || !newEntity) return [];

  const invalidated = new Set<number>();
  Object.keys(rules).forEach((field) => {
    if (valuesDiffer(oldEntity[field], newEntity[field])) {
      rules[field].forEach((step) => invalidated.add(step));
    }
  });

  return [...invalidated].sort((a, b) => a - b);
}

/**
 * List-based variant: caller passes in the names of fields it knows
 * changed (e.g. an editor component that already detected the diff
 * internally), and gets back the sorted union of steps to unmark.
 *
 * Use this when the caller cannot easily snapshot the entity for a
 * full diff but does know which fields were touched — e.g. the
 * AdapterEditor save path computes per-field changes against its
 * pre-edit snapshot internally and forwards just the names through
 * onSaveSuccess. Field names not present in `rules` are silently
 * skipped (same conservative default as computeInvalidatedSteps).
 */
export function stepsForChangedFields(
  changedFields: readonly string[],
  rules: Record<string, number[]>,
): number[] {
  const invalidated = new Set<number>();
  changedFields.forEach((field) => {
    rules[field]?.forEach((step) => invalidated.add(step));
  });
  return [...invalidated].sort((a, b) => a - b);
}

/**
 * Filter out optional steps (Notifications, Actions, MonitorGrid) from an
 * invalidation list when their corresponding wizard state holds no user
 * content. These steps auto-complete from `computeHydratedCompletedSteps`
 * once Users & Roles is done; invalidating them after an upstream change
 * forces the operator to re-walk pages they never put data on. The
 * smart-invalidation rules still kick in once the operator actually
 * creates a notification, action, or monitor grid binding.
 */
export function filterEmptyOptionalSteps(
  steps: readonly number[],
  hasContent: {
    notifications: boolean;
    actions: boolean;
    monitorGrid: boolean;
  },
): number[] {
  return steps.filter((s) => {
    if (s === WizardStep.Notifications && !hasContent.notifications) return false;
    if (s === WizardStep.Actions && !hasContent.actions) return false;
    if (s === WizardStep.MonitorGrid && !hasContent.monitorGrid) return false;
    return true;
  });
}
